/**
 * Sign-in page — the user's entry point to MIC.
 *
 * Critical: the "Sign in with Google" action is a plain `<a>` tag
 * pointing at `${VITE_API_URL}/api/auth/google/login`, **not** a
 * fetch or `button onClick`. It has to be a top-level browser
 * navigation so the browser follows the chain of redirects
 * (MIC API → Google consent → MIC API callback → back here) and
 * accepts the Set-Cookie headers along the way. A fetch() would
 * follow the redirects in JS, but cookies set during an XHR are
 * often blocked by the browser's cross-origin policies.
 *
 * The page also honors an optional `?auth_error=...` query param.
 * When the callback handler can't complete the flow (user declined
 * consent, state mismatch, token validation failed) it redirects
 * here with an error code, and we render a matter-of-fact message.
 */

import { useSearchParams } from "react-router-dom";

import { BrandMark } from "@/components/BrandMark";
import { Wordmark } from "@/components/Wordmark";
import { API_URL } from "@/lib/api";

const SIGN_IN_URL = `${API_URL}/api/auth/google/login`;

const DEFAULT_AUTH_ERROR = "Sign-in didn't complete. Try again.";

function resolveAuthError(code: string | null): string | null {
  if (!code) return null;
  // Inlined switch instead of a Record<string,string> lookup — the `Record`
  // approach trips `noUncheckedIndexedAccess` because both `record[key]` and
  // `record.default` return `string | undefined` under that flag.
  switch (code) {
    case "access_denied":
      return "You declined sign-in. Try again whenever you're ready.";
    case "state_mismatch":
      return "Sign-in link expired. Start again — this usually happens if you came back after more than 10 minutes.";
    default:
      return DEFAULT_AUTH_ERROR;
  }
}

export function SignInPage() {
  const [searchParams] = useSearchParams();
  const errorMessage = resolveAuthError(searchParams.get("auth_error"));

  return (
    <main className="max-w-[640px] mx-auto px-6 md:px-11 pt-24 pb-16 flex flex-col items-start gap-8">
      <div className="flex items-center gap-4">
        <BrandMark />
        <Wordmark size="hero" />
      </div>

      <div className="flex flex-col gap-3">
        <div className="font-sans text-eyebrow uppercase text-coral-deep font-semibold">
          Sign in
        </div>
        <h1 className="font-serif text-display-md text-ink">
          <em className="italic font-normal text-coral-deep">Time with your kids</em> is finite.
        </h1>
        <p className="text-[15px] text-ink-muted leading-[1.6] max-w-[560px]">
          Sign in with your Google account to see each child's Perspectives —
          18 summers, Santa years, family dinners, everything — on one
          dashboard, updated every time you open the app.
        </p>
      </div>

      <a
        href={SIGN_IN_URL}
        className="inline-flex items-center gap-3 px-6 py-3 rounded-md font-sans font-semibold text-white no-underline shadow-md transition-transform hover:-translate-y-[1px] focus:outline-none focus-visible:ring-2 focus-visible:ring-amber"
        style={{
          background: "linear-gradient(135deg, var(--coral), var(--coral-deep))",
          boxShadow: "0 4px 12px rgba(251,118,84,0.35)",
        }}
      >
        <GoogleLogo />
        Sign in with Google
      </a>

      {errorMessage && (
        <div
          className="w-full max-w-[560px] rounded-md border border-coral-deep/30 bg-coral-soft/40 px-5 py-4"
          role="alert"
        >
          <div className="font-sans text-[13px] font-semibold uppercase tracking-[0.14em] text-coral-deep mb-1">
            Sign-in issue
          </div>
          <div className="font-sans text-[14px] text-ink-soft">{errorMessage}</div>
        </div>
      )}

      <p className="font-sans text-[12px] text-ink-muted italic max-w-[560px]">
        Make It Count is invitation-only. New accounts need super-admin approval
        before they can use the app — if you're the second parent accepting an
        invitation, your link bypasses that gate automatically.
      </p>
    </main>
  );
}

function GoogleLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#ffffff"
        d="M21.35 11.1h-9.17v2.97h5.26c-.25 1.48-1.77 4.33-5.26 4.33-3.17 0-5.76-2.62-5.76-5.85s2.59-5.85 5.76-5.85c1.8 0 3.01.77 3.7 1.43l2.52-2.43C16.7 4.16 14.7 3.2 12.18 3.2 6.95 3.2 2.82 7.34 2.82 12.55s4.13 9.35 9.36 9.35c5.4 0 8.98-3.8 8.98-9.14 0-.61-.07-1.08-.17-1.66z"
      />
    </svg>
  );
}
